import axios from './axiosInstance';
import nhisPublicAxios from './nhisPublicAxios';
import { log, LogLevel } from 'utils/LogUtil';
import {
  NhisTransactionStatus,
  ConsentInfo,
  Consent,
  AuthMethodType,
  NHIS_ERROR_MESSAGES,
} from 'types/nhis';
import { PrescriptionGroup } from 'common/models/types';

// --- 공통 응답 타입 ---

interface NhisResponse<T = any> {
  error: number;  // 0 = 성공, 7100-7121 = NHIS 에러 코드, -1 = 클라이언트 에러
  data?: T;
  message?: string;
}

// --- 약사용 API (JWT 인증 필요, axiosInstance 사용) ---

/** 건강정보 요청 생성 */
export async function createNhisRequest(params: {
  patientId: number;
  patientName: string;
  phoneNumber: string;
}): Promise<NhisResponse<{
  transactionId: string;
  status: string;
  consentUrl: string;
}>> {
  try {
    const response = await axios.post('/api/v1/nhis/request', params);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, 'NHIS 요청 생성 실패:', error);
    return { error: -1, message: '건강정보 요청 생성에 실패했습니다.' };
  }
}

/** 요청 상태 조회 */
export async function fetchNhisRequestStatus(
  transactionId: string
): Promise<NhisResponse<NhisTransactionStatus>> {
  try {
    const response = await axios.get(`/api/v1/nhis/request/${transactionId}`);
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, 'NHIS 요청 상태 조회 실패:', error);
    return { error: -1, message: '요청 상태를 조회할 수 없습니다.' };
  }
}

/** 환자별 NHIS 요청 목록 조회 (약사용, 최근 2일 이내) */
export async function fetchNhisRequestsByPatient(
  patientId: number,
  limit: number = 5
): Promise<NhisResponse<NhisTransactionStatus[]>> {
  try {
    const twoDaysAgo = new Date();
    twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);

    const response = await axios.get('/api/v1/nhis/requests', {
      params: {
        patientId,
        limit,
        startDate: twoDaysAgo.toISOString(),
      },
    });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, 'NHIS 요청 목록 조회 실패:', error);
    return { error: -1, message: '요청 목록을 조회할 수 없습니다.' };
  }
}

/** 최근 N일 전체 NHIS 요청 목록 (대시보드 전용, patientId 미지정) */
export async function fetchRecentNhisRequests(
  daysAgo: number = 2
): Promise<NhisResponse<NhisTransactionStatus[]>> {
  try {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - daysAgo);

    const response = await axios.get('/api/v1/nhis/requests', {
      params: {
        startDate: startDate.toISOString(),
      },
    });
    return response.data;
  } catch (error) {
    log(LogLevel.ERROR, '최근 NHIS 요청 조회 실패:', error);
    return { error: -1, message: '최근 NHIS 요청 조회에 실패했습니다.' };
  }
}

/** 요청 취소 (약사용, PENDING/AUTH_REQUESTED/FETCHED/FAILED 상태에서만 가능) */
export async function cancelNhisRequest(
  transactionId: string
): Promise<NhisResponse<null>> {
  try {
    const response = await axios.delete(`/api/v1/nhis/request/${transactionId}`);
    return response.data;
  } catch (error: any) {
    log(LogLevel.ERROR, 'NHIS 요청 취소 실패:', error);
    const errorCode = error.response?.data?.error;
    return {
      error: errorCode || -1,
      message: error.response?.data?.message || '요청 취소에 실패했습니다.',
    };
  }
}

// --- 환자용 API (인증 불필요, nhisPublicAxios 사용) ---

/** 동의 페이지 정보 조회 */
export async function fetchConsentInfo(
  token: string
): Promise<NhisResponse<ConsentInfo>> {
  try {
    const response = await nhisPublicAxios.get(`/api/v1/nhis/consent/${token}`);
    return response.data;
  } catch (error: any) {
    log(LogLevel.ERROR, '동의 정보 조회 실패:', error);
    const errorCode = error.response?.data?.error;
    return {
      error: errorCode || -1,
      message: NHIS_ERROR_MESSAGES[errorCode] || '정보를 불러올 수 없습니다.',
    };
  }
}

/** 본인인증 시작 (간편인증 / 휴대폰 본인인증, 동의 상태 포함) */
export async function startNhisAuth(params: {
  token: string;
  authMethodType?: AuthMethodType;
  loginType?: string;
  juminNum: string;
  telecomType?: string;
  consent?: Consent;
}): Promise<NhisResponse<{ status: string; message: string }>> {
  try {
    const response = await nhisPublicAxios.post('/api/v1/nhis/auth/start', params);
    return response.data;
  } catch (error: any) {
    log(LogLevel.ERROR, '본인인증 시작 실패:', error);
    const errorCode = error.response?.data?.error;
    const serverMessage = error.response?.data?.message;
    return {
      error: errorCode || -1,
      // 7117은 서버 message(동적 Octover 원본 메시지)가 더 구체적이므로 우선 사용
      message: errorCode === 7117 && serverMessage
        ? serverMessage
        : (NHIS_ERROR_MESSAGES[errorCode] || '인증 요청에 실패했습니다.'),
    };
  }
}

/** 인증 확인 + 데이터 조회 (PHONE_VERIFY일 때는 certificationNum 필수) */
export async function confirmNhisAuth(params: {
  token: string;
  certificationNum?: string;
}): Promise<NhisResponse<{
  status?: string;
  prescriptionGroups?: PrescriptionGroup[];
  detailCode?: string;
  detailMessage?: string;
}>> {
  try {
    const response = await nhisPublicAxios.post('/api/v1/nhis/auth/confirm', params);
    return response.data;
  } catch (error: any) {
    log(LogLevel.ERROR, '인증 확인 실패:', error);
    const errorCode = error.response?.data?.error;
    const serverMessage = error.response?.data?.message;
    const errorData = error.response?.data?.data;
    return {
      error: errorCode || -1,
      // 7117은 서버 message(동적 Octover 원본 메시지)가 더 구체적이므로 우선 사용
      message: errorCode === 7117 && serverMessage
        ? serverMessage
        : (NHIS_ERROR_MESSAGES[errorCode] || '인증 확인에 실패했습니다.'),
      ...(errorData ? { data: errorData } : {}),
    };
  }
}

/** 선택한 처방전 전달 */
export async function deliverPrescriptions(params: {
  token: string;
  prescriptionGroups: PrescriptionGroup[];
}): Promise<NhisResponse<{ status: string; deliveredCount: number; message: string }>> {
  try {
    const response = await nhisPublicAxios.post('/api/v1/nhis/prescriptions/deliver', params);
    return response.data;
  } catch (error: any) {
    log(LogLevel.ERROR, '처방전 전달 실패:', error);
    const errorCode = error.response?.data?.error;
    return {
      error: errorCode || -1,
      message: NHIS_ERROR_MESSAGES[errorCode] || '처방전 전달에 실패했습니다.',
    };
  }
}
